import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { registerVisitorSession } from '../../api/client';
import { trackPageView } from '../../utils/analytics';
import { startVisitBeacons } from '../../utils/visitBeacon';

/**
 * Registers the visitor session once per browsing session AND sends a GA4
 * page_view on every client-side navigation (akkubolt's PageViewTracker role).
 *
 * Session: without it this site sent no X-SESSION-ID, so content_manager#GetOneBySlug
 * could not resolve a session and skipped BOTH the Blog_View statistic and the
 * `session_article` row that ties an order back to the articles its session read.
 *
 * Page views: gtag.js is configured with `send_page_view: false`, so without the
 * location effect only the initial document load would ever be counted — every
 * route the visitor reaches through React Router would be invisible. The hit is
 * deferred by one frame because react-helmet-async writes the new <title> in its
 * own effect; sending synchronously would attach the *previous* route's title.
 *
 * Renders nothing; registerVisitorSession() is idempotent, fire-and-forget, and skips the
 * prerender pass.
 */
export default function SessionTracker() {
	const { pathname, search } = useLocation();

	useEffect(() => {
		registerVisitorSession();
		// visit_js / visit_interact — a visit counts only when JavaScript ran (Direkt cleanup 2026-09-27).
		return startVisitBeacons();
	}, []);

	useEffect(() => {
		const frame = requestAnimationFrame(() => trackPageView(pathname + search));
		return () => cancelAnimationFrame(frame);
	}, [pathname, search]);

	return null;
}
